import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus, ChevronRight } from "lucide-react";
import { api, fmtMoney } from "@/lib/api";
import { PageHeader, StatusBadge, EmptyState, Loading, ImportBadge, DocDate } from "@/components/common";
import { Button } from "@/components/ui/button";

export const FilterPills = ({ options, value, onChange, testId }) => (
  <div className="flex gap-1.5 overflow-x-auto pb-1 mb-4 -mx-1 px-1" data-testid={testId}>
    {options.map(([v, l]) => <button key={v} onClick={() => onChange(v)} data-testid={`${testId}-${v}`} className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${value === v ? "bg-[#0B192C] text-white border-[#0B192C]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>{l}</button>)}
  </div>
);

export const DocList = ({ items, to, testId, dateKey = "created_at", amountKey = "total", sub, loading, emptyTitle = "Nothing here yet", emptyHint }) => loading ? <Loading /> : items.length === 0 ? <EmptyState title={emptyTitle} hint={emptyHint} /> : (
  <div className="card-flat divide-y divide-slate-100" data-testid={testId}>
    {items.map((d) => (
      <Link to={`${to}/${d.id}`} key={d.id} className="flex items-center gap-3 p-4 table-row-hover" data-testid={`${testId}-row`}>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap"><span className="font-semibold text-slate-900">{d.customer_name || d.customer?.name}</span><span className="text-xs font-mono text-slate-400">{d.number}</span><ImportBadge doc={d} /></div>
          <div className="text-xs text-slate-500 truncate">{sub ? sub(d) : d.line_items?.map((l) => l.description).join(", ")}</div>
        </div>
        <div className="text-right shrink-0 flex flex-col items-end gap-1">
          <span className="font-semibold tabular-nums">{fmtMoney(d[amountKey])}</span>
          <div className="flex items-center gap-2"><DocDate doc={d} dateKey={dateKey} className="text-[11px] text-slate-400" /><StatusBadge status={d.status} /></div>
        </div>
        <ChevronRight size={16} className="text-slate-300" />
      </Link>
    ))}
  </div>
);

export default function Quotes() {
  const [f, setF] = useState("all");
  const { data: quotes = [], isLoading } = useQuery({ queryKey: ["quotes"], queryFn: () => api.get("/quotes").then((r) => r.data) });
  const list = quotes.filter((q) => f === "all" || (f === "open" ? ["sent", "viewed"].includes(q.status) : q.status === f));
  return (
    <div data-testid="quotes-page">
      <PageHeader title="Quotes" subtitle={`${quotes.filter((q) => ["sent", "viewed"].includes(q.status)).length} awaiting approval`} action={<Button asChild className="btn-orange h-10" data-testid="quotes-new-button"><Link to="/quotes/new"><Plus size={16} /> New Quote</Link></Button>} />
      <FilterPills testId="quotes-filter" value={f} onChange={setF} options={[["all", "All"], ["draft", "Draft"], ["open", "Sent / Viewed"], ["accepted", "Accepted"], ["declined", "Declined"]]} />
      <DocList items={list} to="/quotes" testId="quotes-list" loading={isLoading} emptyTitle={f === "all" ? "No quotes yet" : "No quotes match this filter"} emptyHint={f === "all" ? "Create your first quote and email it to a customer." : undefined} />
    </div>
  );
}
